import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { Chip } from '@/components/Chip/Chip';
import { Alert } from '@/components/Alert/Alert';
import { FilterChip } from '@/components/FilterChip/FilterChip';
import { createColumnHelper, DataTable, type ColumnDef } from '@/components/DataTable/DataTable';
import { usePositionsCatalog, useUnitsCatalog } from '@/features/members';
import { t } from '@/lib/i18n';
import { formatRelativeTime } from '@/lib/format';
import { useDeleteJobTemplate, useJobTemplatesList } from '../hooks';
import type { JobTemplate, TenantJobTemplate } from '../schemas';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import styles from './JobTemplateList.module.css';

type SourceFilter = 'all' | 'tenant' | 'system';

const ALL = '__all__';
const columnHelper = createColumnHelper<JobTemplate>();

export function JobTemplateList() {
  const navigate = useNavigate();
  const list = useJobTemplatesList();
  const del = useDeleteJobTemplate();
  const positionsCatalog = usePositionsCatalog();
  const unitsCatalog = useUnitsCatalog();
  const [pendingDelete, setPendingDelete] = useState<TenantJobTemplate | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [departmentFilter, setDepartmentFilter] = useState<string>(ALL);
  const [positionFilter, setPositionFilter] = useState<string>(ALL);

  const items: JobTemplate[] = useMemo(() => list.data?.items ?? [], [list.data]);
  const tenantCount = items.filter((i) => i.kind === 'tenant').length;
  const systemCount = items.filter((i) => i.kind === 'system').length;

  const positionsById = useMemo(() => {
    const map = new Map<string, string>();
    (positionsCatalog.data?.items ?? []).forEach((p) => map.set(p.id, p.name));
    return map;
  }, [positionsCatalog.data]);

  const unitsById = useMemo(() => {
    const map = new Map<string, string>();
    (unitsCatalog.data?.items ?? []).forEach((u) => map.set(u.id, u.name));
    return map;
  }, [unitsCatalog.data]);

  const departmentOptions = useMemo(() => {
    const referenced = new Set<string>();
    items.forEach((i) => i.defaultUnitIds.forEach((id) => referenced.add(id)));
    return Array.from(referenced)
      .map((id) => ({ id, name: unitsById.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, unitsById]);

  const positionOptions = useMemo(() => {
    const referenced = new Set<string>();
    items.forEach((i) => i.defaultPositionIds.forEach((id) => referenced.add(id)));
    return Array.from(referenced)
      .map((id) => ({ id, name: positionsById.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, positionsById]);

  const filteredItems = useMemo(() => {
    return items.filter((i) => {
      if (sourceFilter !== 'all' && i.kind !== sourceFilter) return false;
      if (departmentFilter !== ALL && !i.defaultUnitIds.includes(departmentFilter)) return false;
      if (positionFilter !== ALL && !i.defaultPositionIds.includes(positionFilter)) return false;
      return true;
    });
  }, [items, sourceFilter, departmentFilter, positionFilter]);

  function onConfirmDelete() {
    if (!pendingDelete) return;
    setDeleteError(null);
    del.mutate(
      { id: pendingDelete.id },
      {
        onSuccess: () => setPendingDelete(null),
        onError: (err) => setDeleteError(formatDeleteError(err)),
      },
    );
  }

  const columns: ColumnDef<JobTemplate, unknown>[] = [
    columnHelper.accessor((item) => item.title, {
      id: 'title',
      header: t('Title'),
      cell: (info) => {
        const item = info.row.original;
        return (
          <Link
            to={`/library/job-profiles/${encodeURIComponent(item.id)}`}
            className={styles.nameCell}
          >
            <span className={styles.name}>{item.title}</span>
            {item.description ? <span className={styles.desc}>{item.description}</span> : null}
          </Link>
        );
      },
    }),
    columnHelper.accessor((item) => item.kind, {
      id: 'source',
      header: t('Source'),
      cell: (info) => (
        <Chip variant="neutral" size="sm">
          {info.getValue() === 'tenant' ? t('Yours') : t('Linqage')}
        </Chip>
      ),
      sortingFn: 'basic',
    }),
    columnHelper.accessor((item) => item.updatedAt, {
      id: 'updatedAt',
      header: t('Updated'),
      cell: (info) => formatRelativeTime(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        const item = info.row.original;
        if (item.kind !== 'tenant') return null;
        return (
          <div className={styles.rowActions}>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(item)}>
              {t('Delete')}
            </Button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className={styles.wrap}>
      {list.isError ? <Alert variant="error">{t("Couldn't load Job Profiles.")}</Alert> : null}

      <DataTable
        data={filteredItems}
        columns={columns}
        ariaLabel={t('Job Profiles')}
        defaultSort={{ id: 'title' }}
        isLoading={list.isLoading}
        emptyState={
          sourceFilter === 'all' && departmentFilter === ALL && positionFilter === ALL
            ? t('No Job Profiles available yet.')
            : t('No matches for this filter.')
        }
        searchPlaceholder={t('Search Job Profiles…')}
        searchAccessor={(row) => `${row.title} ${row.description ?? ''}`}
        filterChips={
          <>
            <SelectFilter
              label={t('Department')}
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions}
            />
            <SelectFilter
              label={t('Position')}
              value={positionFilter}
              onChange={setPositionFilter}
              options={positionOptions}
            />
          </>
        }
        subFilterChips={
          <>
            <FilterChip
              label={t('All')}
              count={items.length}
              isActive={sourceFilter === 'all'}
              onClick={() => setSourceFilter('all')}
            />
            <FilterChip
              label={t('Yours')}
              count={tenantCount}
              isActive={sourceFilter === 'tenant'}
              onClick={() => setSourceFilter('tenant')}
            />
            <FilterChip
              label={t('Linqage')}
              count={systemCount}
              isActive={sourceFilter === 'system'}
              onClick={() => setSourceFilter('system')}
            />
          </>
        }
        toolbarActions={
          <Button
            variant="primary"
            size="md"
            onClick={() => void navigate('/library/job-profiles/new')}
          >
            {t('+ New Job Profile')}
          </Button>
        }
      />

      {pendingDelete ? (
        <DeleteConfirmDialog
          itemLabel={pendingDelete.title}
          memberCount={0}
          memberNoun={t('Members')}
          isPending={del.isPending}
          error={deleteError}
          onCancel={() => setPendingDelete(null)}
          onConfirm={onConfirmDelete}
        />
      ) : null}
    </div>
  );
}

function formatDeleteError(err: unknown): string {
  if (err instanceof Error) {
    if ('status' in err) {
      const status = (err as { status: number }).status;
      if (status === 404) return t('This Job Profile is no longer available.');
      if (status === 403) return t("You don't have permission to delete Job Profiles.");
      if (status === 409) return t('Cannot delete a Linqage-managed Job Profile.');
    }
  }
  return t('Delete failed. Try again.');
}

interface SelectFilterProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: readonly { id: string; name: string }[];
}

function SelectFilter({ label, value, onChange, options }: SelectFilterProps) {
  return (
    <label className={styles.selectFilter}>
      <span className={styles.selectLabel}>{label}</span>
      <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value={ALL}>{t('All')}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );
}
